// Mobile-first header navigation drawer built with shadcn Sheet.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Menu } from 'lucide-react';
import { cn } from '@/utilities/cn';
import { useAuth } from '../../hooks';
import { analyticsService } from '../../services/analyticsService';
import { Button } from '../ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import NavItemLabel from './NavItemLabel';
import { getMobileNavSections } from '@/app/routing/navSections';
import { isNavPathActive } from '@/app/routing/getFrontendRoutes';

type HamburgerMenuProps = {
  /** Retained for call sites. */
  tone?: 'home' | 'solid';
};

const HamburgerMenu: React.FC<HamburgerMenuProps> = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logUserOut } = useAuth();
  const sections = useMemo(() => getMobileNavSections(Boolean(user)), [user]);

  const mobileItemClassName = cn(
    'group/nav-item block w-full cursor-pointer rounded-lg px-1 py-1 text-[1.375rem] font-semibold leading-tight tracking-tight no-underline transition-colors sm:text-[1.625rem]',
    'text-popover-foreground hover:text-primary'
  );
  const mobileDestructiveItemClassName = cn(
    'group/nav-item block w-full cursor-pointer rounded-lg bg-transparent px-1 py-1 text-left text-[1.375rem] font-semibold leading-tight tracking-tight transition-colors appearance-none sm:text-[1.625rem]',
    'text-destructive-foreground hover:text-destructive'
  );

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const handleOpenChange = (next: boolean) => {
    analyticsService.trackNavigationMenuToggle(
      'mobile_header',
      next ? 'open' : 'closed',
      {
        surface: 'mobile_navigation',
        nav_source: 'mobile',
        is_mobile: true,
        source_path: location.pathname,
      }
    );
    setIsOpen(next);
  };

  const handleMenuItemClick = (
    label: string,
    sectionKey: string,
    path: string,
    external?: boolean
  ) => {
    analyticsService.trackNavigationItemClick(label, {
      surface: 'mobile_navigation',
      menu_name: 'mobile_header',
      nav_section: sectionKey,
      nav_source: 'mobile',
      is_mobile: true,
      navigation_target: path,
      link_type: external ? 'external' : 'internal',
      source_path: location.pathname,
    });
  };

  const handleLogout = async () => {
    analyticsService.trackNavigationItemClick('Log Out', {
      surface: 'mobile_navigation',
      menu_name: 'mobile_header',
      nav_section: 'account',
      nav_source: 'mobile',
      is_mobile: true,
      navigation_target: '/library',
      link_type: 'internal',
      source_path: location.pathname,
    });

    try {
      await logUserOut();
    } catch {
      // Best-effort logout: still return the user to library.
    } finally {
      navigate('/library');
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon-lg"
          aria-label="Open navigation menu"
          className="size-12 rounded-2xl border-transparent bg-transparent text-foreground shadow-none hover:text-foreground lg:hidden [&_svg]:size-7"
        >
          <Menu />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        className="gap-0 border-l border-border bg-popover/96 p-0 text-popover-foreground backdrop-blur-xl lg:hidden"
      >
        <SheetTitle className="sr-only">Site navigation</SheetTitle>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-6 pt-16">
          <nav aria-label="Mobile navigation" className="flex flex-col gap-8">
            {sections.map((section) => (
              <section key={section.key} className="flex flex-col gap-4">
                <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {section.title}
                </h2>
                <ul className="flex flex-col gap-2">
                  {section.items.map((item) => {
                    if (item.kind === 'link') {
                      const isActive =
                        !item.external &&
                        isNavPathActive(location.pathname, item.path);
                      const isMailto = item.path.startsWith('mailto:');

                      return (
                        <li key={item.path}>
                          {item.external ? (
                            <SheetClose asChild>
                              <a
                                href={item.path}
                                target={isMailto ? undefined : '_blank'}
                                rel={
                                  isMailto ? undefined : 'noopener noreferrer'
                                }
                                onClick={() =>
                                  handleMenuItemClick(
                                    item.label,
                                    section.key,
                                    item.path,
                                    item.external
                                  )
                                }
                                className={mobileItemClassName}
                              >
                                <NavItemLabel
                                  label={item.label}
                                  external
                                  iconClassName="text-muted-foreground"
                                />
                              </a>
                            </SheetClose>
                          ) : (
                            <SheetClose asChild>
                              <Link
                                to={item.path}
                                onClick={() =>
                                  handleMenuItemClick(
                                    item.label,
                                    section.key,
                                    item.path,
                                    item.external
                                  )
                                }
                                aria-current={isActive ? 'page' : undefined}
                                className={cn(
                                  mobileItemClassName,
                                  isActive && 'text-primary'
                                )}
                              >
                                <NavItemLabel label={item.label} />
                              </Link>
                            </SheetClose>
                          )}
                        </li>
                      );
                    }

                    return (
                      <li key={item.action}>
                        <SheetClose asChild>
                          <button
                            type="button"
                            onClick={handleLogout}
                            className={
                              item.destructive
                                ? mobileDestructiveItemClassName
                                : mobileItemClassName
                            }
                          >
                            <NavItemLabel label={item.label} />
                          </button>
                        </SheetClose>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </nav>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default HamburgerMenu;
